import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { HubReactionCounts, HubReactionType } from "@/lib/hub/reactions";

/**
 * LR HUB reactions.
 *
 * COUNTS: denormalized on `hub_posts.reaction_counts` (jsonb, per type),
 * maintained by the `hub_post_reactions_sync` trigger. This hook layers a
 * local optimistic delta on top of the server counts so picking is instant.
 *
 * WRITES: the client writes ONLY its own row in `hub_post_reactions`
 * (RLS: user_id = auth.uid()). Reactions carry no free text, so they need
 * no AI moderation.
 *
 * ISOLATION: touches hub_post_reactions only.
 */

type Delta = Partial<Record<HubReactionType, number>>;

export function useHubReactions(postIds: string[]) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();

  const ids = useMemo(() => Array.from(new Set(postIds)).sort(), [postIds]);

  // The caller's own reaction per post (one row max per post).
  const mineQuery = useQuery({
    queryKey: ["hub-my-reactions", userId ?? "anon", ids.join(",")],
    enabled: !!userId && ids.length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<Record<string, HubReactionType>> => {
      const { data, error } = await supabase
        .from("hub_post_reactions")
        .select("post_id, reaction_type")
        .eq("user_id", userId!)
        .in("post_id", ids);
      if (error) throw error;
      const map: Record<string, HubReactionType> = {};
      for (const r of data ?? []) map[r.post_id] = r.reaction_type as HubReactionType;
      return map;
    },
  });

  // Optimistic overlays (cleared implicitly when the server data catches up).
  const [mineOverride, setMineOverride] = useState<Record<string, HubReactionType | null>>({});
  const [deltas, setDeltas] = useState<Record<string, Delta>>({});

  const mineFor = useCallback(
    (postId: string): HubReactionType | null => {
      if (postId in mineOverride) return mineOverride[postId];
      return mineQuery.data?.[postId] ?? null;
    },
    [mineOverride, mineQuery.data],
  );

  const countsFor = useCallback(
    (postId: string, base: HubReactionCounts | null | undefined): HubReactionCounts => {
      const out: HubReactionCounts = { ...(base ?? {}) };
      const d = deltas[postId];
      if (d) {
        for (const [k, v] of Object.entries(d)) {
          const next = (out[k as HubReactionType] ?? 0) + (v ?? 0);
          if (next > 0) out[k as HubReactionType] = next;
          else delete out[k as HubReactionType];
        }
      }
      return out;
    },
    [deltas],
  );

  /** Picks, replaces, or removes the caller's reaction on a post. */
  const react = useCallback(
    async (postId: string, type: HubReactionType) => {
      if (!userId) return;
      const current = mineFor(postId);
      const removing = current === type;
      const next = removing ? null : type;

      // Optimistic: apply instantly.
      setMineOverride((p) => ({ ...p, [postId]: next }));
      setDeltas((p) => {
        const d: Delta = { ...(p[postId] ?? {}) };
        if (current) d[current] = (d[current] ?? 0) - 1;
        if (next) d[next] = (d[next] ?? 0) + 1;
        return { ...p, [postId]: d };
      });

      try {
        if (removing) {
          const { error } = await supabase
            .from("hub_post_reactions")
            .delete()
            .eq("post_id", postId)
            .eq("user_id", userId);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("hub_post_reactions")
            .upsert(
              { post_id: postId, user_id: userId, reaction_type: type },
              { onConflict: "post_id,user_id" },
            );
          if (error) throw error;
        }
        qc.invalidateQueries({ queryKey: ["hub-my-reactions"] });
      } catch {
        // Roll the optimistic change back.
        setMineOverride((p) => ({ ...p, [postId]: current }));
        setDeltas((p) => {
          const d: Delta = { ...(p[postId] ?? {}) };
          if (current) d[current] = (d[current] ?? 0) + 1;
          if (next) d[next] = (d[next] ?? 0) - 1;
          return { ...p, [postId]: d };
        });
      }
    },
    [mineFor, qc, userId],
  );

  return { mineFor, countsFor, react, canReact: !!userId };
}

export default useHubReactions;
